import React, { useEffect, useState, useCallback, memo } from "react";
import { Container, Row, Col, Card, Spinner, Dropdown, Nav } from 'react-bootstrap';
import { Chart } from "react-google-charts";
import { useParams, useSearchParams } from "react-router-dom";
import { api } from '../../services/api.js';

// 1. COMPONENTE RELÓGIO ISOLADO
const RelogioHeader = memo(() => {
    const [hora, setHora] = useState(() => new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }));

    useEffect(() => {
        const interval = setInterval(() => {
            setHora(new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }));
        }, 10000);
        return () => clearInterval(interval);
    }, []);

    return <h5 className="fw-bold mb-0 text-white" style={{ fontSize: '0.9rem' }}>{hora}</h5>;
});

// 2. TEMPORIZADOR ISOLADO
const TVTimer = memo(({ aoExpirar }) => {
    const [segundos, setSegundos] = useState(180);

    useEffect(() => {
        const timerInt = setInterval(() => {
            setSegundos(s => {
                if (s <= 1) {
                    if (aoExpirar) aoExpirar();
                    return 180;
                }
                return s - 1;
            });
        }, 1000);
        return () => clearInterval(timerInt);
    }, [aoExpirar]);

    return (
        <div style={{
            position: 'fixed', bottom: '10px', right: '10px', zIndex: 9999,
            backgroundColor: 'rgba(0,0,0,0.85)', color: '#fff', padding: '4px 12px',
            borderRadius: '20px', border: '1px solid #0d6efd', fontSize: '0.75rem',
            fontFamily: 'monospace'
        }}>
            {Math.floor(segundos / 60)}:{String(segundos % 60).padStart(2, '0')}
        </div>
    );
});

// PALETAS DE CORES
const PALETAS = {
    azul: { bgHeader: '#0d6efd', borderCard: '#0d6efd', nome: 'Azul Padrão' },
    verde: { bgHeader: '#198754', borderCard: '#198754', nome: 'Verde Floresta' },
    roxo: { bgHeader: '#6f42c1', borderCard: '#6f42c1', nome: 'Roxo Executivo' },
    laranja: { bgHeader: '#fd7e14', borderCard: '#fd7e14', nome: 'Laranja Alerta' },
    dark: { bgHeader: '#212529', borderCard: '#343a40', nome: 'Modo Escuro' }
};

function DashboardDeosTecnico() {
    const { setor } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();

    const setoresVal = setor || searchParams.get("setores") || searchParams.get("setor") || "";
    
    const [temaNome, setTemaNome] = useState(() => {
        return (searchParams.get("tema") || localStorage.getItem("@Arbor:dashTema") || "azul").toLowerCase();
    });

    const tema = PALETAS[temaNome] || PALETAS.azul;

    const [dados, setDados] = useState(null);
    const [nomeSetorExibicao, setNomeSetorExibicao] = useState("");
    const [loading, setLoading] = useState(true);

    // Estado para alternar no Mobile ("hoje" ou "30d")
    const [abaMobile, setAbaMobile] = useState("hoje");

    const trocarTema = (novoTema) => {
        setTemaNome(novoTema);
        localStorage.setItem("@Arbor:dashTema", novoTema);
        const novosParams = new URLSearchParams(searchParams);
        novosParams.set("tema", novoTema);
        setSearchParams(novosParams);
    };

    useEffect(() => {
        async function buscarNomeSetor() {
            if (!setoresVal) {
                setNomeSetorExibicao("GERAL");
                return;
            }
            try {
                const res = await api.get('/chamados/setores');
                const setorEncontrado = (res.data || []).find(
                    s => String(s.id_setor || s.codigo) === String(setoresVal)
                );
                setNomeSetorExibicao(setorEncontrado ? (setorEncontrado.nome_setor || setorEncontrado.descricao) : `SETOR ${setoresVal}`);
            } catch {
                setNomeSetorExibicao(`SETOR ${setoresVal}`);
            }
        }
        buscarNomeSetor();
    }, [setoresVal]);

    const carregarDados = useCallback(async () => {
        try {
            const res = await api.get('/chamados/dashboard', { params: { setores: setoresVal } });
            setDados(res.data);
        } catch (err) {
            console.error("Erro ao carregar Dashboard:", err);
        } finally {
            setLoading(false);
        }
    }, [setoresVal]);

    useEffect(() => {
        carregarDados();
        const apiInt = setInterval(carregarDados, 180000);
        return () => clearInterval(apiInt);
    }, [carregarDados]);

    if (loading && !dados) return (
        <div className="vh-100 d-flex justify-content-center align-items-center bg-dark text-white">
            <Spinner animation="border" variant="primary" className="me-3" />
            <h5>CARREGANDO MONITORAMENTO COMPLETO...</h5>
        </div>
    );

    const resumoGlobal = dados?.resumo_global?.[0] || {};

    // PÓDIO VISUAL DO TOP 3
    const renderPodioTop3 = (listaRanking) => {
        const top = (listaRanking || []).slice(0, 3);
        const primeiro = top[0];
        const segundo = top[1];
        const terceiro = top[2];

        const formatNome = (item) => item?.nome ? item.nome.split(' ')[0] : (item?.usuario || '');

        return (
            <div className="d-flex align-items-end justify-content-center h-100 p-1">
                {/* 2º LUGAR */}
                <div className="text-center mx-1 flex-fill d-flex flex-column justify-content-end" style={{ height: '85%' }}>
                    {segundo ? (
                        <>
                            <small className="fw-bold text-truncate d-block" style={{ fontSize: '0.6rem' }}>{formatNome(segundo)}</small>
                            <span className="badge bg-secondary mb-1" style={{ fontSize: '0.55rem' }}>⭐ {Number(segundo.media_nota).toFixed(1)}</span>
                        </>
                    ) : <small className="text-muted" style={{ fontSize: '0.55rem' }}>-</small>}
                    <div className="bg-secondary text-white fw-bold d-flex align-items-center justify-content-center rounded-top shadow-sm" style={{ height: '55%', fontSize: '0.75rem' }}>
                        🥈 2º
                    </div>
                </div>

                {/* 1º LUGAR */}
                <div className="text-center mx-1 flex-fill d-flex flex-column justify-content-end" style={{ height: '100%' }}>
                    {primeiro ? (
                        <>
                            <small className="fw-bold text-truncate text-warning d-block" style={{ fontSize: '0.65rem' }}>{formatNome(primeiro)}</small>
                            <span className="badge bg-warning text-dark mb-1 fw-bold" style={{ fontSize: '0.6rem' }}>⭐ {Number(primeiro.media_nota).toFixed(1)}</span>
                        </>
                    ) : <small className="text-muted" style={{ fontSize: '0.55rem' }}>-</small>}
                    <div className="bg-warning text-dark fw-bold d-flex align-items-center justify-content-center rounded-top shadow-sm" style={{ height: '75%', fontSize: '0.85rem' }}>
                        🥇 1º
                    </div>
                </div>

                {/* 3º LUGAR */}
                <div className="text-center mx-1 flex-fill d-flex flex-column justify-content-end" style={{ height: '70%' }}>
                    {terceiro ? (
                        <>
                            <small className="fw-bold text-truncate d-block" style={{ fontSize: '0.65rem' }}>{formatNome(terceiro)}</small>
                            <span className="badge bg-danger mb-1" style={{ fontSize: '0.55rem' }}>⭐ {Number(terceiro.media_nota).toFixed(1)}</span>
                        </>
                    ) : <small className="text-muted" style={{ fontSize: '0.55rem' }}>-</small>}
                    <div className="bg-danger text-white fw-bold d-flex align-items-center justify-content-center rounded-top shadow-sm" style={{ height: '40%', fontSize: '0.7rem' }}>
                        🥉 3º
                    </div>
                </div>
            </div>
        );
    };

    const renderPainelPeriodo = (titulo, resumoData, tempoData, statusData, catData, rankData, resolucaoData, avaliacaoData, rankAvalData, corHeader) => {
        const resumo = resumoData?.[0] || {};
        const tempo = tempoData?.[0] || {};
        const resolucao = resolucaoData?.[0] || {};
        const avaliacao = avaliacaoData?.[0] || {};

        const chartPieOptions = {
            legend: { position: "right", textStyle: { fontSize: 8 } },
            chartArea: { width: '92%', height: '85%' },
            pieHole: 0.35,
            colors: ['#0d6efd', '#6610f2', '#d63384', '#fd7e14', '#198754', '#20c997']
        };

        const chartBarOptions = {
            legend: { position: "none" },
            chartArea: { width: '70%', height: '80%', left: 65 },
            hAxis: { minValue: 0 },
            vAxis: { textStyle: { fontSize: 8, fontWeight: 'bold' } }
        };

        const dataStatus = [
            ["Status", "Total"],
            ...(statusData || []).map(s => [`${s.status_nome} (${s.total})`, s.total])
        ];

        const dataCategorias = [
            ["Categoria", "Total"],
            ...(catData || []).slice(0, 5).map(c => [`${c.descricao} (${c.total})`, c.total])
        ];

        const dataRanking = [
            ["Técnico", "Atendimentos", { role: "style" }, { role: "annotation" }],
            ...(rankData || []).slice(0, 4).map((u, idx) => {
                const cores = ['#0d6efd', '#6610f2', '#6f42c1', '#d63384', '#fd7e14'];
                const primeiroNome = u.nome ? u.nome.split(' ')[0] : (u.usuario || 'Técnico');
                return [
                    primeiroNome,
                    u.total,
                    cores[idx % cores.length],
                    u.total
                ];
            })
        ];

        const dataResolucao = [
            ["Tipo", "Quantidade", { role: "style" }, { role: "annotation" }],
            ["Aceito Direto", resolucao.aceitos_direto || 0, "#198754", resolucao.aceitos_direto || 0],
            ["Não Aceito", resolucao.rejeitados || 0, "#dc3545", resolucao.rejeitados || 0],
            ["Aceito pós Reab.", resolucao.aceitos_apos_reapertura || 0, "#ffc107", resolucao.aceitos_apos_reapertura || 0]
        ];

        return (
            <Col xs={12} md={6} className="h-100 d-flex flex-column pe-2 ps-2">
                <div className="p-1 text-white fw-bold text-center rounded mb-1 text-uppercase" style={{ backgroundColor: corHeader, fontSize: '0.85rem' }}>
                    {titulo}
                </div>

                {/* MÉTRICAS E KPIs DO PERÍODO */}
                <Row className="g-1 mb-1" style={{ minHeight: '40px' }}>
                    <Col xs={4} md={2}>
                        <Card className="h-100 text-center border-0 shadow-sm bg-light justify-content-center p-1">
                            <small className="text-muted" style={{ fontSize: '0.55rem' }}>ABERTOS</small>
                            <span className="fw-bold text-danger" style={{ fontSize: '0.85rem' }}>{resumo.abertos || 0}</span>
                        </Card>
                    </Col>
                    <Col xs={4} md={2}>
                        <Card className="h-100 text-center border-0 shadow-sm bg-light justify-content-center p-1">
                            <small className="text-muted" style={{ fontSize: '0.55rem' }}>AG. USUÁRIO</small>
                            <span className="fw-bold text-info" style={{ fontSize: '0.85rem' }}>{resumo.avaliacao || 0}</span>
                        </Card>
                    </Col>
                    <Col xs={4} md={2}>
                        <Card className="h-100 text-center border-0 shadow-sm bg-light justify-content-center p-1">
                            <small className="text-muted" style={{ fontSize: '0.55rem' }}>ATENDIMENTO</small>
                            <span className="fw-bold text-warning" style={{ fontSize: '0.85rem' }}>{resumo.atendimento || 0}</span>
                        </Card>
                    </Col>
                    <Col xs={4} md={2}>
                        <Card className="h-100 text-center border-0 shadow-sm bg-light justify-content-center p-1">
                            <small className="text-muted" style={{ fontSize: '0.55rem' }}>TMA TOTAL</small>
                            <span className="fw-bold text-dark" style={{ fontSize: '0.8rem' }}>{Number(tempo.tma_total || 0).toFixed(1)}h</span>
                        </Card>
                    </Col>
                    <Col xs={4} md={2}>
                        <Card className="h-100 text-center border-0 shadow-sm bg-light justify-content-center p-1">
                            <small className="text-muted" style={{ fontSize: '0.55rem' }}>ABERT ➔ AVAL</small>
                            <span className="fw-bold text-primary" style={{ fontSize: '0.8rem' }}>{Number(tempo.tma_abertura_avaliacao || 0).toFixed(1)}h</span>
                        </Card>
                    </Col>
                    <Col xs={4} md={2}>
                        <Card className="h-100 text-center border-0 shadow-sm bg-warning bg-opacity-25 justify-content-center border-start border-3 border-warning p-1">
                            <small className="text-dark fw-bold" style={{ fontSize: '0.55rem' }}>MÉDIA AVALIÇÃO</small>
                            <span className="fw-bold text-dark" style={{ fontSize: '0.85rem' }}>
                                ⭐ {Number(avaliacao.media_nota || 0).toFixed(1)}
                            </span>
                        </Card>
                    </Col>
                </Row>

                {/* GRADE COMPLETA COM 6 QUADROS */}
                <Row className="g-1 flex-grow-1" style={{ height: '86%' }}>
                    <Col xs={12} md={6} className="dash-quadro-mobile" style={{ height: '33.3%' }}>
                        <Card className="h-100 border-0 shadow-sm">
                            <Card.Header className="p-1 bg-white fw-bold text-center border-0" style={{ fontSize: '0.65rem' }}>STATUS DETALHADO</Card.Header>
                            <Card.Body className="p-0 position-relative">
                                {dataStatus.length > 1 ? (
                                    <Chart chartType="PieChart" width="100%" height="100%" data={dataStatus} options={chartPieOptions} />
                                ) : <div className="text-center text-muted small mt-2">Sem registros</div>}
                            </Card.Body>
                        </Card>
                    </Col>

                    <Col xs={12} md={6} className="dash-quadro-mobile" style={{ height: '33.3%' }}>
                        <Card className="h-100 border-0 shadow-sm">
                            <Card.Header className="p-1 bg-white fw-bold text-center border-0" style={{ fontSize: '0.65rem' }}>TOP CATEGORIAS</Card.Header>
                            <Card.Body className="p-0 position-relative">
                                {dataCategorias.length > 1 ? (
                                    <Chart chartType="PieChart" width="100%" height="100%" data={dataCategorias} options={chartPieOptions} />
                                ) : <div className="text-center text-muted small mt-2">Sem registros</div>}
                            </Card.Body>
                        </Card>
                    </Col>

                    <Col xs={12} md={6} className="dash-quadro-mobile" style={{ height: '33.3%' }}>
                        <Card className="h-100 border-0 shadow-sm">
                            <Card.Header className="p-1 bg-white fw-bold text-center border-0" style={{ fontSize: '0.65rem' }}>RANKING PRODUTIVIDADE</Card.Header>
                            <Card.Body className="p-0 position-relative">
                                {dataRanking.length > 1 ? (
                                    <Chart chartType="BarChart" width="100%" height="100%" data={dataRanking} options={chartBarOptions} />
                                ) : <div className="text-center text-muted small mt-2">Sem registros</div>}
                            </Card.Body>
                        </Card>
                    </Col>

                    <Col xs={12} md={6} className="dash-quadro-mobile" style={{ height: '33.3%' }}>
                        <Card className="h-100 border-0 shadow-sm">
                            <Card.Header className="p-1 bg-white fw-bold text-center border-0" style={{ fontSize: '0.65rem' }}>ACEITAÇÃO DA RESOLUÇÃO</Card.Header>
                            <Card.Body className="p-0 position-relative">
                                {dataResolucao.length > 1 ? (
                                    <Chart chartType="ColumnChart" width="100%" height="100%" data={dataResolucao} options={chartBarOptions} />
                                ) : <div className="text-center text-muted small mt-2">Sem registros</div>}
                            </Card.Body>
                        </Card>
                    </Col>

                    <Col xs={12} className="dash-quadro-mobile" style={{ height: '33.3%' }}>
                        <Card className="h-100 border-0 shadow-sm">
                            <Card.Header className="p-1 bg-white fw-bold text-center border-0" style={{ fontSize: '0.65rem' }}>TOP 3 SATISFAÇÃO DO USUÁRIO</Card.Header>
                            <Card.Body className="p-1 position-relative">
                                {renderPodioTop3(rankAvalData)}
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </Col>
        );
    };

    return (
        <div className="dash-root-wrapper" style={{ height: '100vh', width: '100vw', backgroundColor: '#f4f6f9', position: 'fixed', top: 0, left: 0 }}>
            {/* Header */}
            <div className="dash-header-mobile text-white border-bottom d-flex justify-content-between align-items-center px-2 px-md-3 shadow-sm" style={{ minHeight: '6vh', backgroundColor: tema.bgHeader }}>
                <div className="d-flex align-items-center gap-1 gap-md-2 flex-wrap">
                    <h6 className="fw-bold mb-0 text-white me-1" style={{ fontSize: '0.85rem' }}>
                        DASHBOARD {nomeSetorExibicao && <span className="text-uppercase">- {nomeSetorExibicao}</span>}
                    </h6>

                    <Dropdown>
                        <Dropdown.Toggle size="sm" variant="light" className="fw-bold border-0 py-0 px-1" style={{ fontSize: '0.7rem' }}>
                            <i className="bi bi-palette-fill me-1"></i> {tema.nome}
                        </Dropdown.Toggle>

                        <Dropdown.Menu>
                            {Object.keys(PALETAS).map((key) => (
                                <Dropdown.Item
                                    key={key}
                                    onClick={() => trocarTema(key)}
                                    active={temaNome === key}
                                    className="d-flex align-items-center justify-content-between small"
                                >
                                    <span>{PALETAS[key].nome}</span>
                                    <div style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: PALETAS[key].bgHeader }} />
                                </Dropdown.Item>
                            ))}
                        </Dropdown.Menu>
                    </Dropdown>
                </div>

                <RelogioHeader />
            </div>

            {/* BARRA SUPERIOR GLOBAL DE TOTAIS */}
            <div className="dash-totais-mobile bg-white border-bottom py-1 px-2 px-md-3 shadow-sm" style={{ minHeight: '7vh' }}>
                <Row className="align-items-center g-1 text-center">
                    <Col xs={12} md={3} className="text-center text-md-start mb-1 mb-md-0">
                        <span className="fw-bold text-uppercase text-secondary" style={{ fontSize: '0.7rem' }}>
                            <i className="bi bi-globe me-1"></i> Totais Globais Ativos:
                        </span>
                    </Col>
                    <Col xs={4} md={3}>
                        <Card className="p-1 border-0 bg-light shadow-sm">
                            <small className="text-muted fw-bold" style={{ fontSize: '0.55rem' }}>ABERTOS</small>
                            <span className="fw-bold text-danger" style={{ fontSize: '0.95rem', lineHeight: '1.1' }}>
                                {resumoGlobal.abertos || 0}
                            </span>
                        </Card>
                    </Col>
                    <Col xs={4} md={3}>
                        <Card className="p-1 border-0 bg-light shadow-sm">
                            <small className="text-muted fw-bold" style={{ fontSize: '0.55rem' }}>AG. USUÁRIO</small>
                            <span className="fw-bold text-info" style={{ fontSize: '0.95rem', lineHeight: '1.1' }}>
                                {resumoGlobal.avaliacao || 0}
                            </span>
                        </Card>
                    </Col>
                    <Col xs={4} md={3}>
                        <Card className="p-1 border-0 bg-light shadow-sm">
                            <small className="text-muted fw-bold" style={{ fontSize: '0.55rem' }}>ATENDIMENTO</small>
                            <span className="fw-bold text-warning" style={{ fontSize: '0.95rem', lineHeight: '1.1' }}>
                                {resumoGlobal.atendimento || 0}
                            </span>
                        </Card>
                    </Col>
                </Row>
            </div>

            {/* SELETOR DE ABAS VISÍVEL APENAS NO MOBILE */}
            <div className="d-md-none bg-white border-bottom p-1 shadow-sm" style={{ zIndex: 10, position: 'relative' }}>
                <Nav variant="pills" activeKey={abaMobile} onSelect={(k) => setAbaMobile(k)} className="nav-justified">
                    <Nav.Item>
                        <Nav.Link eventKey="hoje" className="py-1 fw-bold small">
                            <i className="bi bi-calendar-day me-1"></i> Dia Atual (Hoje)
                        </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                        <Nav.Link eventKey="30d" className="py-1 fw-bold small">
                            <i className="bi bi-calendar-range me-1"></i> Acumulado 30 Dias
                        </Nav.Link>
                    </Nav.Item>
                </Nav>
            </div>

            {/* CONTEÚDO PRINCIPAL DIVIDIDO */}
            <Container fluid className="dash-content-mobile p-2" style={{ height: 'calc(87vh - 42px)' }}>
                {/* VISÃO DESKTOP / TV NATIVA (LADO A LADO) */}
                <Row className="h-100 g-2 d-none d-md-flex">
                    {renderPainelPeriodo("Dia Atual (Hoje)", dados?.resumo_hoje, dados?.tempo_hoje, dados?.status_hoje, dados?.categoria_hoje, dados?.ranking_hoje, dados?.resolucao_hoje, dados?.avaliacao_hoje, dados?.ranking_avaliacao_hoje, tema.bgHeader)}
                    {renderPainelPeriodo("Acumulado 30 Dias", dados?.resumo_30d, dados?.tempo_30d, dados?.status_30d, dados?.categoria_30d, dados?.ranking_30d, dados?.resolucao_30d, dados?.avaliacao_30d, dados?.ranking_avaliacao_30d, "#343a40")}
                </Row>

                {/* VISÃO EXCLUSIVA MOBILE (ALTERNADA PELAS ABAS "HOJE" / "30 DAYS") */}
                <Row className="g-2 d-md-none" style={{ height: '100%', overflowY: 'auto' }}>
                    {abaMobile === "hoje" && renderPainelPeriodo("Dia Atual (Hoje)", dados?.resumo_hoje, dados?.tempo_hoje, dados?.status_hoje, dados?.categoria_hoje, dados?.ranking_hoje, dados?.resolucao_hoje, dados?.avaliacao_hoje, dados?.ranking_avaliacao_hoje, tema.bgHeader)}
                    {abaMobile === "30d" && renderPainelPeriodo("Acumulado 30 Dias", dados?.resumo_30d, dados?.tempo_30d, dados?.status_30d, dados?.categoria_30d, dados?.ranking_30d, dados?.resolucao_30d, dados?.avaliacao_30d, dados?.ranking_avaliacao_30d, "#343a40")}
                </Row>
            </Container>

            {/* TEMPORIZADOR ISOLADO */}
            <TVTimer aoExpirar={carregarDados} />
        </div>
    );
}

export default DashboardDeosTecnico;